<template>
    <div class="legal-page">
        <div class="legal-container">
            <header class="legal-header">
                <router-link to="/login" class="back-link">
                    <i class="bi bi-arrow-left"></i> Back
                </router-link>
                <TenantLogo id="legal-logo" logo-class="legal-logo" />
                <h1>Terms and Conditions</h1>
                <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
            </header>

            <main class="legal-content">
                <section>
                    <h2>1. Agreement to Terms</h2>
                    <p>
                        By accessing or using the {{ appName }} application provided by {{ clubName }} ("the Club,"
                        "we," "our,"
                        or "us"), you agree to be bound by these Terms and Conditions. If you do not agree to these
                        terms,
                        please do not use our services.
                    </p>
                    <p>
                        These terms apply to all users of the application, including players, parents/guardians,
                        coaches,
                        and administrative staff.
                    </p>
                </section>

                <section>
                    <h2>2. User Registration and Accounts</h2>
                    <h3>2.1 Account Creation</h3>
                    <p>To use {{ appName }}, you must:</p>
                    <ul>
                        <li>Provide accurate and complete registration information</li>
                        <li>Maintain the security of your account credentials</li>
                        <li>Be at least 18 years old, or have parental/guardian consent</li>
                        <li>Notify us immediately of any unauthorised use of your account</li>
                    </ul>

                    <h3>2.2 Account Responsibilities</h3>
                    <p>
                        You are responsible for all activities that occur under your account. {{ clubName }} reserves
                        the
                        right to suspend or terminate accounts that violate these terms or engage in inappropriate
                        behaviour.
                    </p>
                </section>

                <section>
                    <h2>3. Club Membership and Participation</h2>
                    <h3>3.1 Membership Requirements</h3>
                    <p>By registering with {{ clubName }}, you agree to:</p>
                    <ul>
                        <li>Abide by all club rules, policies, and codes of conduct</li>
                        <li>Attend training sessions and matches as required</li>
                        <li>Communicate absences in advance where possible</li>
                        <li>Treat all players, coaches, staff, and officials with respect</li>
                        <li>Pay all required fees and subscriptions on time</li>
                    </ul>

                    <h3>3.2 Player Conduct</h3>
                    <p>All players registered with {{ clubName }} must:</p>
                    <ul>
                        <li>Demonstrate fair play and sportsmanship at all times</li>
                        <li>Follow instructions from coaches and club officials</li>
                        <li>Refrain from any form of discrimination, bullying, or harassment</li>
                        <li>Not use abusive or inappropriate language</li>
                        <li>Respect opponents, referees, and spectators</li>
                    </ul>
                </section>

                <section>
                    <h2>4. Fees and Payments</h2>
                    <h3>4.1 Payment Terms</h3>
                    <p>
                        Membership fees, training fees, and any other charges must be paid according to the payment
                        schedule
                        set by {{ clubName }}. Failure to pay fees may result in suspension of membership privileges.
                    </p>

                    <h3>4.2 Refund Policy</h3>
                    <p>
                        Refunds may be provided at the discretion of {{ clubName }} management. Requests for refunds
                        must
                        be made in writing and will be considered on a case-by-case basis. No refunds will be provided
                        for:
                    </p>
                    <ul>
                        <li>Sessions missed due to player absence</li>
                        <li>Cancellations with less than 24 hours notice</li>
                        <li>Suspension or termination due to breach of terms</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Health and Safety</h2>
                    <h3>5.1 Medical Information</h3>
                    <p>
                        You agree to provide accurate medical information during registration and to inform {{ clubName
                        }}
                        of any changes to your health status that may affect your participation. This includes injuries,
                        illnesses, allergies, and medications.
                    </p>

                    <h3>5.2 Assumption of Risk</h3>
                    <p>
                        Football is a contact sport that carries inherent risks of injury. By participating in
                        {{ clubName }} activities, you acknowledge and accept these risks. While we take reasonable
                        precautions to ensure safety, we cannot guarantee that injuries will not occur.
                    </p>

                    <h3>5.3 Insurance</h3>
                    <p>
                        {{ clubName }} maintains appropriate liability insurance. However, players and parents/guardians
                        are responsible for their own personal accident and health insurance coverage.
                    </p>
                    <p v-if="legal.insuranceNote">{{ legal.insuranceNote }}</p>
                </section>

                <section>
                    <h2>6. Media and Photography</h2>
                    <h3>6.1 Consent for Photography and Video</h3>
                    <p>
                        By agreeing to these terms, you consent to {{ clubName }} photographing and filming training
                        sessions, matches, and events for the following purposes:
                    </p>
                    <ul>
                        <li>Player development and performance analysis</li>
                        <li>Club promotional materials and social media</li>
                        <li>Website and application content</li>
                        <li>Media and press coverage</li>
                    </ul>
                    <p>
                        If you do not wish to be photographed or filmed, please notify us in writing, and we will make
                        reasonable efforts to accommodate your request.
                    </p>
                </section>

                <section>
                    <h2>7. Intellectual Property</h2>
                    <p>
                        All content within {{ appName }}, including but not limited to logos, graphics, text, and
                        software,
                        is the property of {{ clubName }} or its licensors and is protected by intellectual property
                        laws.
                        You may not reproduce, distribute, or create derivative works without our express written
                        permission.
                    </p>
                </section>

                <section>
                    <h2>8. Limitation of Liability</h2>
                    <p>
                        To the maximum extent permitted by law, {{ clubName }} shall not be liable for any indirect,
                        incidental, special, consequential, or punitive damages arising out of or relating to your use
                        of our services. This includes, but is not limited to:
                    </p>
                    <ul>
                        <li>Loss of data or personal information</li>
                        <li>Personal injury sustained during club activities</li>
                        <li>Damage to personal property</li>
                        <li>Any interruption or cessation of services</li>
                    </ul>
                </section>

                <section>
                    <h2>9. Termination</h2>
                    <h3>9.1 Termination by You</h3>
                    <p>
                        You may terminate your account and membership at any time by providing written notice to
                        {{ clubName }}. Any outstanding fees must be paid before account closure.
                    </p>

                    <h3>9.2 Termination by Us</h3>
                    <p>{{ clubName }} reserves the right to terminate or suspend your account for:</p>
                    <ul>
                        <li>Breach of these Terms and Conditions</li>
                        <li>Non-payment of fees</li>
                        <li>Misconduct or inappropriate behaviour</li>
                        <li>Any other reason at our discretion</li>
                    </ul>
                </section>

                <section>
                    <h2>10. Governing Law</h2>
                    <p>
                        These Terms and Conditions shall be governed by and construed in accordance with
                        {{ legal.governingLaw }}. Any disputes arising under these terms shall be subject to
                        the exclusive jurisdiction of {{ legal.courts }}.
                    </p>
                </section>

                <section>
                    <h2>11. Changes to Terms</h2>
                    <p>
                        {{ clubName }} reserves the right to modify these Terms and Conditions at any time. We will
                        notify users of any material changes by posting the updated terms on the application and
                        updating the "Last Updated" date. Your continued use of the application after such changes
                        constitutes acceptance of the new terms.
                    </p>
                </section>

                <section>
                    <h2>12. Contact Information</h2>
                    <p>If you have any questions about these Terms and Conditions, please contact us:</p>
                    <div class="contact-info">
                        <p><strong>{{ clubName }}</strong></p>
                        <p v-if="legal.contactUrl">
                            Web enquiry: <a :href="legal.contactUrl" target="_blank" rel="noopener">{{ legal.contactUrl }}</a>
                        </p>
                        <p v-else-if="legal.region === 'AU'">Please use the enquiry form on the club website.</p>
                        <p v-else>Email: info@{{ clubEmailDomain }}</p>
                    </div>
                </section>

                <section class="acceptance-section">
                    <h2>13. Acceptance</h2>
                    <p>
                        By clicking "I agree" during registration or by using the {{ appName }} application, you
                        acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                    </p>
                </section>
            </main>

            <footer class="legal-footer">
                <p>&copy; {{ currentYear }} {{ clubName }}. All rights reserved.</p>
            </footer>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import TenantLogo from '@/components/TenantLogo/index.vue'
import { getLegalConfig } from '@/config/tenant'

export default {
    name: 'TermsAndConditions',
    components: {
        TenantLogo
    },
    setup() {
        const store = useStore()

        const clubName = computed(() => store.getters.clubName || 'The Club')
        const appName = computed(() => store.getters.appName || 'Player App')
        const clubNameShort = computed(() => store.getters.clubNameShort || 'Club')

        const currentYear = new Date().getFullYear()
        const lastUpdated = 'October 2026'
        const legal = getLegalConfig()

        // Generate email domain from club name
        const clubEmailDomain = computed(() => {
            const name = clubName.value.toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '')
            return `${name}.com`
        })

        return {
            clubName,
            appName,
            clubNameShort,
            currentYear,
            lastUpdated,
            legal,
            clubEmailDomain
        }
    }
}
</script>

<style lang="scss" scoped>
.legal-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 2rem;
}

.legal-container {
    max-width: 900px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.legal-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
    padding: 2rem;
    text-align: center;
    position: relative;

    .back-link {
        position: absolute;
        left: 1.5rem;
        top: 1.5rem;
        color: white;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        opacity: 0.8;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    h1 {
        margin: 1rem 0 0.5rem;
        font-size: 2rem;
    }

    .last-updated {
        opacity: 0.7;
        font-size: 0.9rem;
    }
}

.legal-content {
    padding: 2rem 3rem;

    section {
        margin-bottom: 2rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    h2 {
        color: #1a1a2e;
        font-size: 1.4rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #e0e0e0;
    }

    h3 {
        color: #333;
        font-size: 1.1rem;
        margin: 1.5rem 0 0.75rem;
    }

    p {
        color: #555;
        line-height: 1.7;
        margin-bottom: 1rem;
    }

    ul {
        margin: 1rem 0;
        padding-left: 1.5rem;

        li {
            color: #555;
            line-height: 1.7;
            margin-bottom: 0.5rem;
        }
    }

    .contact-info {
        background: #f8f9fa;
        padding: 1.5rem;
        border-radius: 8px;
        margin-top: 1rem;

        p {
            margin-bottom: 0.5rem;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .acceptance-section {
        background: #e8f5e9;
        padding: 1.5rem;
        border-radius: 8px;
        margin-top: 2rem;

        h2 {
            border-bottom-color: #4caf50;
        }

        p {
            margin-bottom: 0;
        }
    }
}

.legal-footer {
    background: #f8f9fa;
    padding: 1.5rem;
    text-align: center;
    border-top: 1px solid #e0e0e0;

    p {
        color: #666;
        font-size: 0.9rem;
        margin: 0;
    }
}

#legal-logo {
    max-width: 120px;
    margin: 0 auto;
}

@media (max-width: 768px) {
    .legal-page {
        padding: 1rem;
    }

    .legal-content {
        padding: 1.5rem;
    }

    .legal-header {
        .back-link {
            position: static;
            margin-bottom: 1rem;
            justify-content: center;
        }
    }
}
</style>
